import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  CircularProgress,
  Divider,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  Menu,
  MenuItem,
  Select,
  TextField,
  Typography
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { Check, Close } from "@material-ui/icons";

import toastError from "../../errors/toastError";
import useLossReasons from "../../hooks/useLossReasons";
import ConfirmationModal from "../ConfirmationModal";

const useStyles = makeStyles(theme => ({
  select: {
    width: "100%",
    "& .MuiOutlinedInput-root": {
      height: 46
    },
    "& .MuiSelect-outlined": {
      height: 46,
      boxSizing: "border-box",
      display: "flex",
      alignItems: "center",
      paddingTop: 0,
      paddingBottom: 0
    },
    "& .MuiInputLabel-outlined": {
      transform: "translate(14px, 15px) scale(1)"
    },
    "& .MuiInputLabel-outlined.MuiInputLabel-shrink": {
      transform: "translate(14px, -6px) scale(0.75)"
    }
  },
  selectInput: {
    display: "flex",
    alignItems: "center"
  },
  inlineForm: {
    display: "flex",
    alignItems: "center",
    padding: "6px 16px",
    gap: 8,
    minHeight: 36,
    maxHeight: 36,
    boxSizing: "border-box"
  },
  createOption: {
    fontWeight: 600,
    color: "#14A45C",
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-start",
    gap: 8,
    padding: "10px 16px",
    cursor: "pointer",
    outline: "none",
    "&:hover": {
      backgroundColor: theme.palette.action.hover
    }
  },
  menuItem: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between"
  },
  itemContent: {
    display: "flex",
    alignItems: "center",
    gap: 8
  },
  compactTextField: {
    "& .MuiInputBase-root": {
      fontSize: "0.875rem",
      height: 24
    },
    "& .MuiInputBase-input": {
      padding: "2px 0"
    }
  },
  compactIconButton: {
    padding: 0
  },
  helperText: {
    marginTop: theme.spacing(0.5),
    fontSize: "0.75rem"
  }
}));

const LossReasonSelect = ({
  value,
  onChange,
  kanbanBoardId,
  label = "Motivo da perda",
  disabled = false,
  allowCreate = false,
  error = false,
  helperText = ""
}) => {
  const classes = useStyles();
  const { list, create, update, remove } = useLossReasons();
  const [reasons, setReasons] = useState([]);
  const [loading, setLoading] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [name, setName] = useState("");
  const [contextMenu, setContextMenu] = useState(null);
  const [selectedReasonForMenu, setSelectedReasonForMenu] = useState(null);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [reasonToDelete, setReasonToDelete] = useState(null);

  const fetchReasons = useCallback(async () => {
    if (!kanbanBoardId) {
      setReasons([]);
      return;
    }

    setLoading(true);
    try {
      const data = await list({ kanbanBoardId, activeOnly: true });
      setReasons(data);
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  }, [kanbanBoardId, list]);

  useEffect(() => {
    fetchReasons();
    setIsCreating(false);
    setEditingId(null);
    setName("");
  }, [fetchReasons]);

  const safeValue = useMemo(() => {
    if (!value) return "";
    return reasons.some(reason => Number(reason.id) === Number(value)) ? value : "";
  }, [reasons, value]);

  const handleCreateStart = event => {
    event.preventDefault();
    event.stopPropagation();
    setIsCreating(true);
    setEditingId(null);
    setName("");
  };

  const handleSave = async () => {
    const normalizedName = name.trim();
    if (!normalizedName) return;

    try {
      if (editingId) {
        await update(editingId, { kanbanBoardId, name: normalizedName });
        await fetchReasons();
        setEditingId(null);
        setName("");
        return;
      }

      const reason = await create({ kanbanBoardId, name: normalizedName });
      await fetchReasons();
      onChange({ target: { value: reason.id } });
      setIsCreating(false);
      setName("");
    } catch (err) {
      toastError(err);
    }
  };

  const handleKeyDown = event => {
    if (event.key === "Enter") {
      event.preventDefault();
      event.stopPropagation();
      handleSave();
    }
    if (event.key === "Escape") {
      event.preventDefault();
      event.stopPropagation();
      setIsCreating(false);
      setEditingId(null);
      setName("");
    }
  };

  const handleContextMenu = (event, reason) => {
    if (!allowCreate) return;

    event.preventDefault();
    event.stopPropagation();
    setContextMenu({
      mouseX: event.clientX - 2,
      mouseY: event.clientY - 4
    });
    setSelectedReasonForMenu(reason);
  };

  const handleMenuClose = () => {
    setContextMenu(null);
    setSelectedReasonForMenu(null);
  };

  const handleEditClick = () => {
    const reason = selectedReasonForMenu;
    handleMenuClose();

    if (!reason) return;
    setIsCreating(false);
    setEditingId(reason.id);
    setName(reason.name);
    setMenuOpen(true);
  };

  const handleDeleteClick = () => {
    const reason = selectedReasonForMenu;
    handleMenuClose();

    if (!reason) return;
    setReasonToDelete(reason);
    setConfirmDeleteOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!reasonToDelete) return;

    try {
      await remove(reasonToDelete.id, { kanbanBoardId });
      await fetchReasons();
      if (Number(value) === Number(reasonToDelete.id)) {
        onChange({ target: { value: "" } });
      }
    } catch (err) {
      toastError(err);
    } finally {
      setConfirmDeleteOpen(false);
      setReasonToDelete(null);
      setEditingId(null);
      setName("");
    }
  };

  const renderInlineForm = ({ placeholder, onCancel }) => (
    <Box
      className={classes.inlineForm}
      onClick={event => event.stopPropagation()}
      onKeyDown={event => event.stopPropagation()}
    >
      <TextField
        autoFocus
        placeholder={placeholder}
        value={name}
        onChange={event => setName(event.target.value)}
        onKeyDown={handleKeyDown}
        fullWidth
        className={classes.compactTextField}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <IconButton size="small" color="primary" onClick={handleSave} className={classes.compactIconButton}>
                <Check />
              </IconButton>
              <IconButton size="small" onClick={onCancel} className={classes.compactIconButton}>
                <Close />
              </IconButton>
            </InputAdornment>
          )
        }}
      />
    </Box>
  );

  return (
    <>
      <FormControl
        variant="outlined"
        fullWidth
        error={error}
        disabled={disabled || !kanbanBoardId}
        className={classes.select}
      >
        <InputLabel>{label}</InputLabel>
        <Select
          label={label}
          value={safeValue}
          onChange={onChange}
          open={menuOpen}
          onOpen={() => setMenuOpen(true)}
          onClose={() => {
            if (!isCreating && !editingId) setMenuOpen(false);
          }}
          renderValue={selected => {
            const reason = reasons.find(item => Number(item.id) === Number(selected));
            if (!reason) return "";
            return (
              <Box display="flex" alignItems="center" gridGap={8}>
                {reason.name}
              </Box>
            );
          }}
          classes={{ select: classes.selectInput }}
          MenuProps={{
            anchorOrigin: { vertical: "bottom", horizontal: "left" },
            transformOrigin: { vertical: "top", horizontal: "left" },
            getContentAnchorEl: null,
            autoFocus: false,
            style: { zIndex: 9999 }
          }}
        >
          {allowCreate && (
            isCreating ? (
              renderInlineForm({
                placeholder: "Nome do motivo",
                onCancel: () => {
                  setIsCreating(false);
                  setName("");
                }
              })
            ) : (
              <Box
                component="li"
                className={classes.createOption}
                onMouseDown={handleCreateStart}
                style={{ fontSize: "1rem" }}
              >
                <span role="img" aria-label="criar novo">➕</span>
                Criar novo...
              </Box>
            )
          )}

          {allowCreate && <Divider />}

          {loading ? (
            <MenuItem disabled>
              <Box className={classes.itemContent}>
                <CircularProgress size={18} />
                <Typography>Carregando...</Typography>
              </Box>
            </MenuItem>
          ) : reasons.map(reason => (
            editingId === reason.id ? (
              <Box key={reason.id}>
                {renderInlineForm({
                  placeholder: "Nome do motivo",
                  onCancel: () => {
                    setEditingId(null);
                    setName("");
                  }
                })}
              </Box>
            ) : (
              <MenuItem
                key={reason.id}
                value={reason.id}
                className={classes.menuItem}
                onContextMenu={event => handleContextMenu(event, reason)}
              >
                <Box className={classes.itemContent}>
                  <Typography>{reason.name}</Typography>
                </Box>
              </MenuItem>
            )
          ))}
        </Select>
        {helperText ? (
          <Typography className={classes.helperText} color={error ? "error" : "textSecondary"}>
            {helperText}
          </Typography>
        ) : null}
      </FormControl>

      <Menu
        keepMounted
        open={contextMenu !== null}
        onClose={handleMenuClose}
        anchorReference="anchorPosition"
        anchorPosition={
          contextMenu !== null
            ? { top: contextMenu.mouseY, left: contextMenu.mouseX }
            : undefined
        }
        style={{ zIndex: 10002 }}
      >
        <MenuItem onClick={handleEditClick}>Editar</MenuItem>
        <MenuItem onClick={handleDeleteClick}>Excluir</MenuItem>
      </Menu>

      <ConfirmationModal
        title="Excluir motivo de perda"
        open={confirmDeleteOpen}
        onClose={() => {
          setConfirmDeleteOpen(false);
          setReasonToDelete(null);
        }}
        onConfirm={handleConfirmDelete}
      >
        Tem certeza que deseja excluir este motivo de perda?
      </ConfirmationModal>
    </>
  );
};

export default LossReasonSelect;
